import { metadata } from '@/constants/metadata';
import Head from 'next/head';
import { useRouter } from 'next/router';
import React from 'react';
import { seoTags } from '@/components/seo/tags';

const DEFAULT_IMAGE_PATH = '/imgs/qasralawani.jpeg';

// the site wide defaults are per language: the UAE stores ('ar-AE', 'en-AE')
// share the KSA ones, they have no entry of their own
const siteLanguage = (locale) =>
  String(locale || '')
    .toLowerCase()
    .includes('en')
    ? 'en'
    : 'ar';

// a value with text in it, or the fallback: '' and whitespace - what an
// empty Magento field answers - count as missing, never as a blank title.
// The product document wraps its name in a one item array.
const textOr = (value, fallback) => {
  const text = Array.isArray(value) ? value[0] : value;
  return (typeof text === 'string' && text.trim()) || fallback;
};

const Seo = ({
  title,
  description,
  image = null,
  keywords = null,
  // QA-125: the backend `seo` block of this page (canonical_url, hreflang,
  // robots), ready to render as-is. Pages the backend has no block for leave
  // it out and fall back to their own request path.
  seo = null,
  // the site policy's robots value, for the pages it rules on instead of the
  // backend (search results)
  robots = null,
  children,
}) => {
  const { locale, defaultLocale, asPath, query } = useRouter();
  const defaults = metadata.home[siteLanguage(locale)];
  const newTitle = textOr(title, defaults.title);
  const newDescription = textOr(description, defaults.desc);
  const img = textOr(
    image,
    `${process.env.NEXT_PUBLIC_WEBSITE_URL}${DEFAULT_IMAGE_PATH}`
  );
  const tags = seoTags(seo, {
    asPath,
    locale,
    defaultLocale,
    query,
    robots,
  });
  // og:url and twitter:url are the same address the canonical points at
  const url = tags.find(({ key }) => key === 'canonical')?.props?.href;
  return (
    <Head>
      <title>{newTitle}</title>
      {tags}
      {keywords && <meta name='keywords' content={keywords} />}
      {/* QA-315: no <meta name="title"> - not a standard tag, and audit
          tools read it as a second title */}
      <meta name='description' content={newDescription} />

      <meta property='og:type' content='website' />
      <meta
        property='og:url'
        content={url || process.env.NEXT_PUBLIC_WEBSITE_URL}
      />
      <meta property='og:title' content={newTitle} />
      <meta property='og:description' content={newDescription} />
      <meta property='og:image' content={img} />

      <meta name='twitter:card' content='summary_large_image' />
      <meta
        property='twitter:url'
        content={url || process.env.NEXT_PUBLIC_WEBSITE_URL}
      />
      <meta property='twitter:title' content={newTitle} />
      <meta property='twitter:description' content={newDescription} />
      <meta property='twitter:image' content={img} />
      {children}
    </Head>
  );
};

export default Seo;
